import React from 'react';
import { View, StyleSheet } from 'react-native';
import { useTheme } from 'react-native-paper';
import { useTranslation } from 'react-i18next';
import QText from './QText';

const QueueUserHeader = ({
  queueUserDetail,
  showStatus = true,
  isAdmin = false,
  customStyles = {},
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const styles = createStyles(theme);

  if (!queueUserDetail) return null;

  const StatusBadge = () => {
    if (!showStatus) return null;

    const getStatusColor = (status) => {
      switch (status) {
        case 'Confirmed':
          return '#4CAF50';
        case 'Checked-in':
          return '#2196F3';
        case 'Removed':
        case 'Exited':
          return '#F44336';
        default:
          return '#FF9800';
      }
    };

    return (
      <View
        style={[
          styles.statusBadge,
          { backgroundColor: getStatusColor(queueUserDetail.status) },
        ]}
      >
        <QText style={styles.statusText}>{queueUserDetail.status}</QText>
      </View>
    );
  };

  return (
    <View style={[styles.container, customStyles.container]}>
      {/* Status Badge */}
      <StatusBadge />

      {/* Queue Name */}
      <View style={styles.queueNameContainer}>
        <QText style={[styles.queueName, customStyles.queueName]}>
          {queueUserDetail.queue_name || queueUserDetail.queue?.name}
        </QText>
      </View>

      {/* Batch/Token Display */}
      {queueUserDetail.queue_type === 'Batch' ? (
        <>
          {/* Batch Badge */}
          {queueUserDetail.batch_name && (
            <View style={styles.batchContainer}>
              <View style={styles.batchBadge}>
                <QText style={styles.batchText}>
                  {queueUserDetail.batch_name}
                </QText>
              </View>
            </View>
          )}

          {/* Party Size */}
          <View style={styles.partySizeContainer}>
            <QText style={styles.partySizeLabel}>{t('party_size')}</QText>
            <QText style={styles.partySizeValue}>
              {queueUserDetail.party_size}
            </QText>
          </View>
        </>
      ) : (
        /* Token Number for non-batch queues */
        <View style={styles.tokenContainer}>
          <QText style={styles.tokenLabel}>{t('token_no')}</QText>
          <View style={styles.tokenBadge}>
            <QText style={styles.tokenNumber}>
              {queueUserDetail.token_number}
            </QText>
          </View>
        </View>
      )}
    </View>
  );
};

const createStyles = (theme) =>
  StyleSheet.create({
    container: {
      alignItems: 'center',
      paddingVertical: 20,
    },
    statusBadge: {
      paddingHorizontal: 16,
      paddingVertical: 8,
      borderRadius: 20,
      marginBottom: 16,
    },
    statusText: {
      color: '#FFFFFF',
      fontSize: 14,
      fontWeight: '600',
    },
    queueNameContainer: {
      marginBottom: 16,
    },
    queueName: {
      fontSize: 24,
      fontWeight: 'bold',
      color: theme.colors.whiteTxt,
      textAlign: 'center',
    },
    batchContainer: {
      marginBottom: 16,
    },
    batchBadge: {
      backgroundColor: theme.colors.primary,
      paddingHorizontal: 24,
      paddingVertical: 16,
      borderRadius: 12,
      minWidth: 120,
      alignItems: 'center',
    },
    batchText: {
      fontSize: 20,
      fontWeight: 'bold',
      color: theme.colors.onPrimary || '#FFFFFF',
    },
    partySizeContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 12,
    },
    partySizeLabel: {
      fontSize: 16,
      color: theme.colors.whiteTxt,
    },
    partySizeValue: {
      fontSize: 18,
      fontWeight: '600',
      color: theme.colors.whiteTxt,
    },
    tokenContainer: {
      alignItems: 'center',
    },
    tokenLabel: {
      fontSize: 16,
      color: theme.colors.whiteTxt,
      marginBottom: 8,
    },
    tokenBadge: {
      backgroundColor: theme.colors.primary,
      paddingHorizontal: 24,
      paddingVertical: 16,
      borderRadius: 12,
      minWidth: 100,
      alignItems: 'center',
    },
    tokenNumber: {
      fontSize: 24,
      fontWeight: 'bold',
      color: theme.colors.onPrimary || '#FFFFFF',
    },
  });

export default QueueUserHeader;
